'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import MobileFooter from './MobileFooter';

const Footer = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 1024); // Footer break is usually LG (1024)
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const links = {
        explore: [
            { label: 'Home', href: '/' },
            { label: 'Services', href: '/services' },
            { label: 'Our Work', href: '/portfolio' },
            { label: 'About Us', href: '/about' },
        ],
        legal: [
            { label: 'Privacy Policy', href: '/privacy' },
            { label: 'Terms of Service', href: '/terms' },
            { label: 'Cookie Policy', href: '/cookies' },
        ]
    };

    const contact = [
        {
            icon: 'EnvelopeIcon',
            text: 'sitemapgenius@gmail.com',
            href: 'mailto:sitemapgenius@gmail.com'
        },
        {
            icon: 'PhoneIcon',
            text: '+91 8686284396',
            href: 'tel:+918686284396'
        },
        {
            icon: 'ChatBubbleLeftRightIcon',
            text: 'WhatsApp Chat',
            href: 'https://wa.me/918686284396?text=Hi%20SitemapGenius,%20I%27m%20interested%20in%20your%20services.'
        },
        {
            icon: 'MapPinIcon',
            text: 'Hyderabad India',
            href: 'https://www.google.com/maps/search/?api=1&query=Hyderabad+India'
        }
    ];

    if (isMobile) {
        return <MobileFooter />;
    }

    return (
        <footer className="bg-white py-10 border-t border-slate-100">
            <div className="container mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">

                    {/* Brand Column - Spans 4 columns */}
                    <div className="lg:col-span-4 flex flex-col items-start gap-3">
                        <h3 className="text-xl font-bold text-slate-900">
                            SitemapGenius
                        </h3>
                        <p className="text-sm text-slate-500 font-medium max-w-xs">
                            We Don't Just Build; We Elevate
                        </p>
                    </div>

                    {/* Quick Links - Spans 3 columns */}
                    <div className="lg:col-span-3 lg:pl-4">
                        <h4 className="uppercase tracking-wider text-xs font-bold text-slate-400 mb-4">
                            Explore
                        </h4>
                        <ul className="space-y-2">
                            {links.explore.map((link, i) => (
                                <li key={i}>
                                    <a href={link.href} className="text-sm font-medium text-slate-600 hover:text-indigo-600 transition-colors flex items-center group">
                                        <span className="w-0 group-hover:w-2 h-0.5 bg-indigo-600 mr-0 group-hover:mr-2 transition-all duration-300"></span>
                                        {link.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info - Spans 3 columns */}
                    <div className="lg:col-span-3">
                        <h4 className="uppercase tracking-wider text-xs font-bold text-slate-400 mb-4">
                            Contact
                        </h4>
                        <ul className="space-y-3">
                            {contact.map((item, i) => (
                                <li key={i} className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300 flex-shrink-0">
                                        <Icon name={item.icon as any} className="w-4 h-4" />
                                    </div>
                                    <div className="flex flex-col">
                                        {item.href ? (
                                            <a
                                                href={item.href}
                                                target={item.href.startsWith('http') ? '_blank' : undefined}
                                                rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                className="text-sm font-medium text-slate-700 hover:text-indigo-600 transition-colors"
                                            >
                                                {item.text}
                                            </a>
                                        ) : (
                                            <span className="text-sm font-medium text-slate-700">{item.text}</span>
                                        )}
                                        {i === 1 && <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide mt-0.5">Support Line</span>}
                                        {i === 2 && <span className="text-[10px] text-green-600 font-bold uppercase tracking-wide mt-0.5">Fast Response</span>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal Links - Spans 2 columns */}
                    <div className="lg:col-span-2">
                        <h4 className="uppercase tracking-wider text-xs font-bold text-slate-400 mb-4">
                            Legal
                        </h4>
                        <div className="space-y-2">
                            {links.legal.map((link, i) => (
                                <a key={i} href={link.href} className="block text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors">
                                    {link.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="pt-6 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-3 text-xs font-medium text-slate-400">
                    <p className="text-xs font-medium text-slate-400 hover:text-indigo-600 transition-colors duration-300 cursor-default">&copy; 2022 SitemapGenius. All rights reserved.</p>
                    <p className="flex items-center flex-wrap gap-1">
                        design and developed by{' '}
                        <a
                            href="https://instagram.com/abdulsohailsamad"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 font-bold transition-colors"
                        >
                            Abdul Sohail Samad
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
